import interceptor from '../common/interceptor';
import { APIPaths } from '../common/constants';
import type { ApiResponse, PaginatedApiResponse } from '@/apis/types';
import type { CompanyTeam, CreateTeamModel } from './settings';

/** A team's group, on rows from the Settings → Teams list and team saves. */
export interface TeamGroupFields {
  /** `null` when the team is in no group. */
  group_id?: string | null;
  group_name?: string | null;
}

/**
 * A row of the Settings → Teams list: a team, or (`is_group`) a group heading
 * whose counts aggregate its teams.
 */
export type SettingsTeamRow = CompanyTeam &
  TeamGroupFields & {
    is_group?: boolean;
    /** Group rows only: how many of the user's visible teams it holds. */
    teams_count?: number;
  };

/**
 * Settings → Teams page (`view=tree`): page 1 starts with every group the user
 * can see, then the teams in them, then the ungrouped teams of the page.
 */
export type SettingsTeamsPageResponse = PaginatedApiResponse<
  SettingsTeamRow[]
> & {
  data: {
    data: {
      /** Whether the user may manage groups (default admins only). */
      can_manage_groups?: boolean;
    };
  };
};

export const GetSettingsTeamsPage = (
  token: string,
  search = '',
  page = 1
): Promise<SettingsTeamsPageResponse> => {
  const params = new URLSearchParams({ view: 'tree', page: String(page) });
  if (search) params.set('search', search);
  return new Promise((resolve, reject) => {
    interceptor
      .axiosGet('Get', `${APIPaths.teams}?${params.toString()}`, token, null)
      .then((res) => resolve(res))
      .catch((error) => {
        console.error('GetSettingsTeamsPage Error:', error?.response);
        reject(error?.response);
      });
  });
};

/** Team create/update with a group; only default admins may send `group_id`. */
export type CreateTeamWithGroupModel = CreateTeamModel & {
  /** `null` takes the team out of its group. */
  group_id?: string | null;
};

/** A group and the ids of its teams the user can see. */
export interface TeamGroup {
  id: string;
  name: string;
  color: string;
  team_ids: string[];
}

const TEAM_GROUPS_PATH = '/auth/team-groups/';

/** Per attempt, so a hung request can't block every picker's next refresh. */
const TEAM_GROUPS_TIMEOUT_MS = 15_000;

/**
 * Groups with the user's visible teams in each, sorted by name. Pickers expand
 * a group to these teams and only ever save team ids. Default admins also get
 * empty groups, so a team form can add a first team.
 */
export const GetTeamGroups = (
  token: string
): Promise<ApiResponse<TeamGroup[]>> => {
  return new Promise((resolve, reject) => {
    interceptor
      // Optional data: no error toast (an older backend has no endpoint), but
      // a 401 still signs the user out.
      .axiosGet(
        'Get',
        TEAM_GROUPS_PATH,
        token,
        null,
        undefined,
        true,
        TEAM_GROUPS_TIMEOUT_MS
      )
      .then((res) => resolve(res))
      .catch((error) => {
        console.error('GetTeamGroups Error:', error?.response);
        reject(error?.response);
      });
  });
};

export interface TeamGroupModel {
  name: string;
  color: string;
}

export type TeamGroupRow = TeamGroupModel & { id: string };

export const CreateTeamGroup = (
  token: string,
  model: TeamGroupModel
): Promise<ApiResponse<TeamGroupRow>> => {
  return new Promise((resolve, reject) => {
    interceptor
      .axiosPost('Post', TEAM_GROUPS_PATH, model, token)
      .then((res) => resolve(res))
      .catch((error) => {
        console.error('CreateTeamGroup Error:', error?.response);
        reject(error?.response);
      });
  });
};

export const UpdateTeamGroup = (
  token: string,
  id: string,
  model: Partial<TeamGroupModel>
): Promise<ApiResponse<TeamGroupRow>> => {
  return new Promise((resolve, reject) => {
    interceptor
      .axiosPost('Patch', `${TEAM_GROUPS_PATH}${id}/`, model, token)
      .then((res) => resolve(res))
      .catch((error) => {
        console.error('UpdateTeamGroup Error:', error?.response);
        reject(error?.response);
      });
  });
};

/** Deletes the group; its teams become ungrouped. */
export const DeleteTeamGroup = (
  token: string,
  id: string
): Promise<ApiResponse<{ id: string; ungrouped_teams: number }>> => {
  return new Promise((resolve, reject) => {
    interceptor
      .axiosDelete('Delete', `${TEAM_GROUPS_PATH}${id}/`, null, token)
      .then((res) => resolve(res))
      .catch((error) => {
        console.error('DeleteTeamGroup Error:', error?.response);
        reject(error?.response);
      });
  });
};
